import React, { FC, useRef } from "react";
import useAdminUserCreateModel from "./useAdminUserCreateModel";
import { Wrapper } from "../components/Wrapper";
import Loading from "app/pages/home/components/Loading";
import { Row } from "react-bootstrap";
import { FormInput } from "../components/FormInput";
import { InputCheckbox } from "components/InputCheckbox";
import { Alert, Box, Divider, Paper, Typography } from "@mui/material";
import QuizMultiSelector from "../components/QuizMultiSelector";
import GroupForm from "components/GroupForm";
import FormActions from "app/pages/admin/components/FormActions";
import { Button } from "app/pages/admin/components";
import { StepperHeader, StepperContent } from "./components";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles(() => ({
  quizSection: {
    padding: "20px",
    border: "2px dashed #e0e0e0",
    borderRadius: "8px",
  },
}));

const AdminUserCreateView: FC<ReturnType<typeof useAdminUserCreateModel>> = ({
  translate,
  loading,
  formData,
  consumptionOptions,
  currentCountry,
  userTypesData,
  activeStep,
  steps,
  createdUser,
  quizzesSelected,
  loadingQuizUpdate,
  setActiveStep,
  submit,
  onCleanForm,
  handleChangeDate,
  handleChangeSenha,
  handleChangeSettingsPassword,
  handleChange,
  isResearchOrDemo,
  IsValidName,
  IsValidEmail,
  verifyCpfCnpj,
  toggleShowPassword,
  formIsValid,
  handleToggleAtivo,
  handleSetGrouper,
  handleSetUsersLicense,
  handleSetDevicesLicense,
  handleSetGrouperId,
  setQuizzesSelected,
  updateQuizzes,
  handleFinish,
}) => {
  const classes = useStyles();

  const renderUserForm = () => (
    <Wrapper
      className="card-form"
      headerTitle={translate("screens.user.mainData")}
    >
      <Loading isLoading={loading} />
      <div className="card-body">
        <form onSubmit={submit}>
          <Row>
            <FormInput
              label={translate("screens.user.labels.name")}
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={translate("screens.user.placeholders.name")}
              errorMessage={translate("screens.category.labels.invalide.name")}
              isInvalid={formData.name ? !IsValidName(formData.name) : false}
              className="col-lg-4"
            />

            <FormInput
              label={translate("screens.user.labels.email")}
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              placeholder={translate("screens.user.placeholders.email")}
              errorMessage={translate(
                "screens.forgotPassword.error.invalidEmailTitle",
              )}
              isInvalid={formData.email ? !IsValidEmail(formData.email) : false}
              className="col-lg-4"
              maxLength={100}
            />

            <FormInput
              label={translate("screens.user.labels.profile")}
              name="profile"
              value={formData.profile}
              onChange={handleChange}
              required
              placeholder={translate("screens.user.placeholders.profile")}
              className="col-lg-4"
              type="select"
              disable={formData.disableProfile}
              Options={
                <>
                  {!formData.profile && (
                    <option value="" disabled>
                      {translate("screens.user.placeholders.profile")}
                    </option>
                  )}
                  {formData.profiles.map(p => (
                    <option key={p.id} value={p.nome}>
                      {p.nome}
                    </option>
                  ))}
                </>
              }
            />
          </Row>

          <Row>
            <FormInput
              label={translate("screenApp.listPacient.account.phone")}
              name="mobilePhone"
              value={formData.mobilePhone}
              onChange={handleChange}
              placeholder={translate("screenApp.createAccount.phone")}
              className="col-lg-2"
              maxLength={14}
            />

            {formData.country === "BR" ? (
              <FormInput
                label={currentCountry}
                name="tin"
                value={verifyCpfCnpj(formData.tin).cpfCnpjFormat}
                onChange={handleChange}
                placeholder={translate("screens.user.placeholders.cpf")}
                errorMessage={verifyCpfCnpj(formData.tin).error}
                isInvalid={!verifyCpfCnpj(formData.tin).isValid}
                className="col-lg-2"
                maxLength={14}
              />
            ) : (
              <FormInput
                label={currentCountry}
                name="document"
                value={formData.document}
                onChange={handleChange}
                placeholder={translate("screens.user.placeholders.document")}
                className="col-lg-2"
                maxLength={14}
              />
            )}

            <FormInput
              label={translate(
                "screenApp.createAccount.profile.nextPaymentDate",
              )}
              name="nextPaymentDate"
              value={formData.nextPaymentDate}
              onChange={e =>
                handleChangeDate(e as React.ChangeEvent<HTMLInputElement>)
              }
              required={!isResearchOrDemo()}
              className="col-lg-4"
              type="date"
            />

            <FormInput
              label={translate("screens.user.labels.formConsumption")}
              name="consumptionControl"
              value={formData.consumptionControl}
              onChange={handleChange}
              required
              className="col-lg-4"
              type="select"
              Options={
                <>
                  {consumptionOptions.map(option => (
                    <option key={option.key} value={option.key}>
                      {option.label}
                    </option>
                  ))}
                </>
              }
            />
          </Row>

          <Row>
            <FormInput
              label="Senha de Login"
              name="senha"
              value={formData.senha}
              onChange={handleChangeSenha}
              placeholder="Digite a senha de login"
              required={true}
              className="col-lg-4"
              type={!formData.showPassword ? "password" : "text"}
            />

            <FormInput
              label={translate("screens.user.labels.pin")}
              name="settingsPassword"
              value={formData.settingsPassword}
              onChange={handleChangeSettingsPassword}
              placeholder={translate("screens.user.labels.insert.pin")}
              required={false}
              className="col-lg-4"
              type={!formData.showPassword ? "password" : "text"}
            />

            <InputCheckbox
              checked={formData.showPassword}
              onChange={toggleShowPassword}
              label={translate("screens.user.labels.showPin")}
            />
          </Row>

          <Row>
            <InputCheckbox
              checked={formData.ativo}
              onChange={handleToggleAtivo}
              label="Usuário Ativo"
            />

            <FormInput
              label={translate("labels_user_type")}
              name="userType"
              value={formData.userType}
              onChange={handleChange}
              required
              className="col-lg-4"
              type="select"
              Options={
                <>
                  <option value={""}>
                    {translate("screens_user_consumptionControl_optionsSelect")}
                  </option>
                  {userTypesData.map(option => (
                    <option key={option.value} value={option.value}>
                      {translate(option.translationKey)}
                    </option>
                  ))}
                </>
              }
            />
          </Row>

          <Row>
            <GroupForm
              isGrouper={formData.grouper}
              setIsGrouper={handleSetGrouper}
              usersLicense={formData.usersLicense}
              setUsersLicense={handleSetUsersLicense}
              devicesLicense={formData.devicesLicense}
              setDevicesLicense={handleSetDevicesLicense}
              grouperId={formData.grouperId}
              setGrouperId={handleSetGrouperId}
            />
          </Row>

          <FormActions
            module="users"
            isEdit={false}
            formIsValid={formIsValid()}
            onCleanForm={onCleanForm}
          />
        </form>
      </div>
    </Wrapper>
  );

  const renderQuizSection = () => (
    <Paper className={`${classes.quizSection} card-form`}>
      <div className="card-body">
        <Typography variant="h6" gutterBottom className="header-form">
          {translate("labels_save_membership")}
        </Typography>

        {createdUser && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {translate("screens.user.create.quiz.userCreated")}:{" "}
            <strong>{createdUser.nome}</strong> ({createdUser.email})
          </Alert>
        )}

        {/* Componente de seleção múltipla de quizzes */}
        <QuizMultiSelector
          selectedQuizzes={quizzesSelected}
          onSelectionChange={setQuizzesSelected}
          disabled={loadingQuizUpdate}
        />

        <Box mt={3} display="flex" gap={2} alignItems="center">
          <Button
            disabled={quizzesSelected.length === 0 || loadingQuizUpdate}
            onClick={updateQuizzes}
          >
            {loadingQuizUpdate
              ? translate("screens_loading_title")
              : translate(
                  "screens_user_create_quiz_associate",
                  `Associar ${quizzesSelected.length} Quiz(zes)`,
                ).replace("{{quizCount}}", quizzesSelected.length.toString())}
          </Button>

          {quizzesSelected.length > 0 && (
            <Typography variant="body2" color="textSecondary">
              {quizzesSelected.length}{" "}
              {translate("screens_quiz_selection_selected")}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box display="flex" justifyContent="space-between" mt={3}>
          <Button
            styleHover={{
              backgroundColor: "#e0e0e0",
              color: "#000",
            }}
            style={{
              backgroundColor: "#f0f0f0",
              color: "#333",
            }}
            onClick={() => setActiveStep(0)}
          >
            {translate("buttons_back", "Voltar")}
          </Button>

          <Button
            styleHover={{ backgroundColor: "#369c3a", color: "#fff" }}
            style={{ backgroundColor: "#42a147", color: "#fff" }}
            onClick={handleFinish}
          >
            {translate("screenApp_quiz_result_buttonFinish", "Finalizar")}
          </Button>
        </Box>
      </div>
    </Paper>
  );

  return (
    <Box sx={{ width: "100%", padding: 0 }}>
      <Loading isLoading={loading} />

      <Box paddingTop={4} marginBottom={2}>
        <Typography
          variant="h4"
          gutterBottom
          style={{ fontSize: "1.5em", fontWeight: "bold" }}
        >
          {// Novo Usuário e Associação de Testes
          translate("screens.user.create.admin.title", "Novo Usuário")}
        </Typography>

        <Typography variant="body1" color="textSecondary" paragraph>
          {translate(
            "screens.user.create.admin.description",
            "Crie um novo usuário e associe-o a testes existentes.",
          )}
        </Typography>
      </Box>

      <StepperHeader steps={steps} activeStep={activeStep} />

      {activeStep === 0 && renderUserForm()}
      {activeStep === 1 && renderQuizSection()}
    </Box>
  );
};

export default AdminUserCreateView;
